import { useCallback, useEffect, useMemo, useState } from 'react';
import { useWeeklyProgressQuery } from '@lingoda/monolith-graphql';
import { useSelector } from '@lingoda/hooks';
import { userCreatedAtSelector } from '@lingoda/auth';
import { studentWeekStartPreference } from '@lingoda/students';
import { Suspense } from '@lingoda/ui';
import { WeeklyProgressLayout, type WeeklyProgressLayoutProps } from './WeeklyProgressLayout';

interface WeeklyProgressProps extends LayoutProps {}

type LayoutProps = Pick<WeeklyProgressLayoutProps, 'paperProps'>;

export const WeeklyProgress = (props: WeeklyProgressProps) => {
    const [page, setPage] = useState(0);
    const [currentGoal, setCurrentGoal] = useState<number | null>(null);
    const { data, loading } = useWeeklyProgressQuery({
        fetchPolicy: 'cache-first',
        nextFetchPolicy: 'cache-and-network',
        variables: {
            page,
        },
    });
    const userRegisterDate = useSelector(userCreatedAtSelector);
    const weekStartsOn = useSelector(studentWeekStartPreference);

    const hasPrevious = data?.studentGoalWidgetData.hasPrevious;
    const hasNext = data?.studentGoalWidgetData.hasNext;

    const handleNavigationLeft = useCallback(() => setPage((page) => page + 1), []);
    const handleNavigationRight = useCallback(() => setPage((page) => page - 1), []);

    useEffect(() => {
        if (page === 0 && !!data) {
            setCurrentGoal(data?.activeGoal?.value ?? null);
        }
    }, [page, userRegisterDate, weekStartsOn, data]);

    const chartItems = useMemo(
        () =>
            (data?.studentGoalWidgetData.weeklyProgress ?? [])
                .map(({ attendance, goal, shortLabel, longLabel }, index) => ({
                    id: `${page}-${index}`,
                    attendance,
                    goal,
                    shortLabel,
                    longLabel,
                }))
                .reverse(),
        [data?.studentGoalWidgetData.weeklyProgress, page],
    );

    return (
        <Suspense variant="circular">
            <WeeklyProgressLayout
                loading={loading && !data}
                currentGoal={currentGoal}
                monthRange={data?.studentGoalWidgetData.range ?? ''}
                page={page}
                chartItems={chartItems}
                onNavigationLeft={hasPrevious ? handleNavigationLeft : undefined}
                onNavigationRight={hasNext ? handleNavigationRight : undefined}
                paperProps={props.paperProps}
            />
        </Suspense>
    );
};
